import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {  Units, colors, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../../../Styles';
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes, SelectFieldOption_Values, NewFieldStyles, NewFieldTypes } from '../../../../Definitions'
import { ButtonList, Button, NewSelect } from '../../../../Components';
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ReactiveTable } from '../../../../Components/Tables/reactiveTableRenderer';
import { useEditableColumnConstructor } from '../../../../Components/Tables/_Tables/useEditableColumnConstructor';
import { cloneDeep, keyBy, merge, partition } from 'lodash';
import { DayComponent, FeedCurve } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall';
import { MaintainFeedCurve, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveUpdates } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall';
import { DynamicDataObk, Graph_QL } from '../../../../Services';
import { convertFeedCurveToTableData, FeedCurveTableData, handleNewFeedComponent, handlePercentileEdit, handleRemoveComponent, valueIsNumberValue, valueIsSelectFieldOptionsValue } from '../../../../Components/FeedCurves/_FeedCurves/feedCurveTableUtils';
import { useFeedComponents } from '../../../../Services/Agrisys_GqlDataCaller';
import { useAppSelector } from '../../../../Store';
import { MenuFloats } from '../../../../Components/BaseComponents/newCreatableSelect';
import { ValueSteps } from '../../../../Components/BaseComponents/newInput';

interface FeedCurveTableProps {
  setDisableCurveNavigation: Dispatch<SetStateAction<boolean>>;
  milestoneOptions: SelectFieldOption_Values[];
  watchFeedCurve?: () => void;
  feedCurve: FeedCurve;
  maintainFeedCurve: (changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurve, optimisticFeedCurve: FeedCurve}) => void;
}


export const FeedCurveTable = ({setDisableCurveNavigation, watchFeedCurve, feedCurve: originalFeedCurve, milestoneOptions, maintainFeedCurve}: FeedCurveTableProps) => {
  const [editTable, setEditTable] = useState(false);
  const createdMilestoneIdRef = useRef<number>(1);
  const createdDayComponentsIdRef = useRef<number>(1);
  const [feedCurve, setFeedCurve] = useState(cloneDeep(originalFeedCurve));
  const data = useMemo(() => {return convertFeedCurveToTableData(feedCurve, setFeedCurve, createdDayComponentsIdRef, createdMilestoneIdRef)}, [feedCurve])
  const [isPercentileEdit, setIsPercentileEdit] = useState(false);
  const {t} = useTranslation();

  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
 
  const {call: getFeedComponentsInfo, data: feedComponentsState} = useFeedComponents(connection);
  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const usedComponentIds = feedCurve.components?.map((component) => component.componentId);
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data.filter((feedComponent) => !feedComponent.deleted && !usedComponentIds?.includes(feedComponent.id)): undefined,[domain, feedComponentsState, usedComponentIds])


  const tableData = useMemo(() => {return Array.from(handlePercentileEdit(data, feedCurve.components, isPercentileEdit).values())},[data, feedCurve.components, isPercentileEdit])
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  useEffect(() => {
    setFeedCurve(cloneDeep(originalFeedCurve));
    setIsPercentileEdit(false);
  },[originalFeedCurve])

  const dirtRef = useRef<MaintainFeedCurve>({feedCurveId: feedCurve.feedCurveId})

  const accessorData = useMemo(() => ([
    {headerValue: t("feedCurvePages.feedCurve.day", {defaultValue: "day"}), accessor: "day", type: ListData_ValueTypes.Number,},
    {headerValue: t("feedCurvePages.components.ds", {defaultValue: "ds"}), accessor: "ds", type: ListData_ValueTypes.Number, staticColumn: true, unit: Units.pct_white},
    {headerValue: t("units.total", {defaultValue: "Total"}), accessor: "totalAmount", type: ListData_ValueTypes.Number, staticColumn: !isPercentileEdit, unit: Units.kG_white},
    {headerValue: t("milestones.general.milestone", {defaultValue: "milestone"}), accessor: "milestone", type: ListData_ValueTypes.List, options: milestoneOptions,},
    {headerValue: "", accessor: "addDay", type: ListData_ValueTypes.Button, width: "42px"},
    {headerValue: "notApplicable", accessor: "removeRowActionColumn", type: ListData_ValueTypes.DeleteRowAction, width: "42px"},
    ...(feedCurve.components??[]).map(({name, componentId, feedCurveComponentId}, index) => ({
      accessor: String(componentId),
      staticColumn: isPercentileEdit,
      unit: isPercentileEdit ? Units.pct_white : Units.kG_white,
      idForColor: componentId,
      type: ListData_ValueTypes.Number,
      valueStep: ValueSteps.floats,
      width: "minmax(150px, 1fr)",
      headerValue: name,
      onClick: (editTable ? () => {setFeedCurve(handleRemoveComponent({ componentId, feedCurveComponentId, componentIndex: index, feedCurve, dirtRef }))} : undefined)
    })),
    {
      headerValue: !editTable ? "" :
      <NewSelect
        onChange={(value) => {setFeedCurve(cloneDeep(handleNewFeedComponent({ feedComponentId: Number(value?.id), feedComponents, dirtRef, feedCurve})))}}
        options = {feedComponents?.map((component) => ({label: component.name, id: component.id})) ?? []}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.transparent,
          fieldType: NewFieldTypes.tableHeaderCell,
          fieldWidth: "25px",
          menuWidth: "150px",
          menuFloat: MenuFloats.center
        }}
        istableHeaderCell
        />,
      accessor: "nonValidAccessorValueAddComponent",
      type: ListData_ValueTypes.Display,
      sort: false,
      width: feedCurve.components?.length === 0 ? undefined : "70px"
    }
  ])
  ,[editTable, feedComponents, feedCurve, isPercentileEdit, milestoneOptions, t])
  const columns = useEditableColumnConstructor<FeedCurveTableData>({accessorData: [...accessorData], tableStyle: TableStyles_Types.primaryTable, canEdit: editTable })
  const table = useReactTable<FeedCurveTableData>({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnOrder: ["milestone","day", "addDay", "removeRowActionColumn", ...(feedCurve.components??[]).map((component)=>(String(component.componentId))), "nonValidAccessorValueAddComponent", "totalAmount", "ds"],
      columnPinning: {
        left: mobileXs? undefined : ["milestone", "day", "addDay", "removeRowActionColumn"],
        right: mobileXs? undefined : ["totalAmount", "ds"],
      },
    },
    meta: {
      // #region Save function
      //TODO CTO REFACTOR -- For creates consider sending the entire feed curve an only create if ids are missing
      updateData: (rowId, columnId, value, row) => {
        const oldRow = data.get(rowId);
        if (feedCurve.milestones) {
          if (columnId === "totalAmount" && valueIsNumberValue(value, columnId) && oldRow) { 
            const deviation = value/oldRow[columnId]
            if (feedCurve.milestones[oldRow.milestoneArrayIndex] && feedCurve.milestones[oldRow.milestoneArrayIndex].feedDays) {
              const currentDay = feedCurve.milestones[oldRow.milestoneArrayIndex].feedDays[oldRow.dayIndex]
              const changes = (feedCurve.components ?? []).reduce((accumulator, currentValue) => {
                const oldAmount = oldRow[currentValue.componentId];
                if (oldAmount && currentDay) {
                  const currentComponent = currentDay.dayComponents.find((component) => (component.componentId === currentValue.componentId));
                  if (currentComponent) {
                    currentComponent.amount = currentComponent.amount*deviation;
                    accumulator.push(currentComponent)
                  }
                }
                return accumulator
              },[] as DayComponent[]);
              if (dirtRef.current.updates === undefined) {
                dirtRef.current.updates={};
              }
              if (dirtRef.current.creates === undefined) {
                dirtRef.current.creates={};
              }
              const [createValueUpdates, existingValueUpdate] = partition(changes, (change) => (change.dayComponentId < 0))
              dirtRef.current.updates.dayComponents = Object.values(merge({}, keyBy(dirtRef.current.updates?.dayComponents, "dayComponentId"), keyBy(existingValueUpdate.map(({amount, dayComponentId}) => ({dayComponentId, baseInfo: {amount}})), "dayComponentId")));
              if (dirtRef.current.creates.milestones && createValueUpdates.length > 0) {
                const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => (milestone.temporaryId === oldRow.milestoneId));
                if (dirtyMilestone && dirtyMilestone.feedDays) {
                  const dirtyDay = dirtyMilestone.feedDays.find((feedDay) => (feedDay.temporaryId === oldRow.id));
                  if (dirtyDay && dirtyDay.dayComponents) {
                    dirtyDay.dayComponents = createValueUpdates.map(({amount, componentId, dayComponentId}) => ({amount, component_id: componentId, temporaryId: dayComponentId}))
                  }
                } 
              }
              setFeedCurve({...feedCurve});
              return
              }
            }
          if (columnId === "milestone" && valueIsSelectFieldOptionsValue(value, columnId) && oldRow) {
            if (oldRow.milestone) {
              if (oldRow.milestoneId > 0) {
                if (dirtRef.current.updates === undefined) {
                  dirtRef.current.updates = {} 
                }
                dirtRef.current.updates.milestones = Object.values(merge({}, keyBy(dirtRef.current.updates.milestones, "feedCurveMilestoneId"), keyBy([{feedCurveMilestoneId: row.original.milestoneId, baseInfo: {fk_milestones: value.id}}], "dayComponentId")))
              } else {
                if (dirtRef.current.creates === undefined) {
                  dirtRef.current.creates = {} 
                }
                if (dirtRef.current.creates.milestones) {
                  const newMilestone = dirtRef.current.creates.milestones.find((milestone) => milestone.temporaryId === oldRow.milestoneId);
                  if (newMilestone) {
                    newMilestone.milestone_id = Number(value.id ?? -1);
                  }
                }
              }
              
              feedCurve.milestones[oldRow.milestoneArrayIndex].milestoneId = Number(value.id ?? -1);
              feedCurve.milestones[oldRow.milestoneArrayIndex].name = value.label;
              setFeedCurve(cloneDeep(feedCurve));
              return
            }

            const [affectedMilestones, remainingMilestones] = partition(feedCurve.milestones, (milestone) => (milestone.milestoneIndex > oldRow.milestoneIndex ));
            const changeAffectedMilestones = affectedMilestones.map((milestone) => ({...milestone, milestoneIndex: milestone.milestoneIndex+1}));
            const feedCurveMilestone = remainingMilestones.find((milestone) => milestone.feedCurveMilestoneId === oldRow.milestoneId);
            const [remainingDays, daysToCreate] = partition(feedCurveMilestone?.feedDays, (feedDay) => ((feedCurveMilestone?.milestoneIndex === 1 ? +feedDay.day : feedDay.day + oldRow.milestoneFirstDay) < oldRow.day));
            if (feedCurveMilestone !== undefined) {
              feedCurveMilestone.feedDays = remainingDays
            }
            feedCurve.milestones = [...remainingMilestones, ...changeAffectedMilestones];
            const newMilestone = {milestoneId: Number(value.id ?? -1), milestoneIndex: oldRow.milestoneIndex+1, name: value.label, feedCurveMilestoneId: -createdMilestoneIdRef.current, feedDays: daysToCreate.map((feedDay) => {createdDayComponentsIdRef.current += 1; return {day: feedDay.day-Math.min(...daysToCreate.map((day) => (day.day))), dayId: -(createdDayComponentsIdRef.current-1), dayComponents: feedDay.dayComponents.map((dayComponent) => ({...dayComponent, dayComponentId: -1 }))};})}
            createdMilestoneIdRef.current += 1;
            feedCurve.milestones.push(newMilestone)
            setFeedCurve(cloneDeep(feedCurve))

            if (dirtRef.current.creates === undefined) {
              dirtRef.current.creates = {};
            }
            if (dirtRef.current.creates.milestones === undefined) {
              dirtRef.current.creates.milestones = [];
            }
            dirtRef.current.creates.milestones = [...dirtRef.current.creates.milestones, {temporaryId: newMilestone.feedCurveMilestoneId, milestone_id: newMilestone.milestoneId, milestone_index: newMilestone.milestoneIndex, feedDays: newMilestone.feedDays.map(({day, dayComponents, dayId}) => ({temporaryId: dayId, day, dayComponents: dayComponents.map(({componentId, amount, dayComponentId}) => ({component_id: componentId, amount, temporaryId: dayComponentId}))}))}]
            return
          }
          if (columnId === "day" && valueIsNumberValue(value, columnId) && oldRow) {
            const feedCurveMilestone = feedCurve.milestones[oldRow.milestoneArrayIndex];
            if (feedCurveMilestone) {
              const day = feedCurveMilestone.feedDays[oldRow.dayIndex]
                day.day = feedCurveMilestone.milestoneIndex === 1 ? +value : value - oldRow.milestoneFirstDay;
                setFeedCurve(cloneDeep(feedCurve));
                if (day.dayId < 0) {
                  if (dirtRef.current.creates && dirtRef.current.creates.milestones) {
                    const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => (milestone.temporaryId === feedCurveMilestone.feedCurveMilestoneId));
                    if (dirtyMilestone && dirtyMilestone.feedDays) {
                      const dirtyDay = dirtyMilestone.feedDays.find((feedDay) => (feedDay.temporaryId === day.dayId));
                      if (dirtyDay) {
                        dirtyDay.day = value - oldRow.milestoneFirstDay;
                      }
                    }
                  }
                } else {
                  if (dirtRef.current.updates === undefined) {
                    dirtRef.current.updates = {};
                  }
                  if (dirtRef.current.updates.feedDays === undefined) {
                    dirtRef.current.updates.feedDays = [];
                  }
                  dirtRef.current.updates.feedDays = Object.values(merge({}, keyBy(dirtRef.current.updates.feedDays, "dayId"), keyBy([{dayId: day.dayId, baseInfo: {day: day.day}}],)))
                }
                return
              }
            }
          if (oldRow && valueIsNumberValue(value, columnId)) {
            const feedCurveMilestone = feedCurve.milestones[oldRow.milestoneArrayIndex]
            if (feedCurveMilestone) {
              const day = feedCurveMilestone.feedDays[oldRow.dayIndex]
              if (day) {
                const dayComponent = day.dayComponents.find((dayComponent) => (dayComponent.componentId === +columnId));
                if (dayComponent) {
                  dayComponent.amount = value*1000;
                  if (dayComponent.dayComponentId<0) {
                    if (dirtRef.current.creates && dirtRef.current.creates.milestones) {
                      const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => milestone.temporaryId === feedCurveMilestone.feedCurveMilestoneId);
                      if (dirtyMilestone && dirtyMilestone.feedDays) {
                        const dirtyDay = dirtyMilestone.feedDays.find((feedDay) => (feedDay.temporaryId === day.dayId));
                        if (dirtyDay) {
                          if (dirtyDay.dayComponents) {
                            const dirtyDayComponent = dirtyDay.dayComponents.find((dirtyDayComponent) => dirtyDayComponent.temporaryId === dayComponent.dayComponentId);
                            if (dirtyDayComponent) {
                              dirtyDayComponent.amount = dayComponent.amount;
                            }
                          } else {
                            dirtyDay.dayComponents = [{temporaryId: dayComponent.dayComponentId, amount: dayComponent.amount, component_id: dayComponent.componentId}]
                          }
                        }
                      }
                    } 
                    
                  } else {
                    if (dirtRef.current.updates === undefined) {
                      dirtRef.current.updates = {};
                    }
                    if (dirtRef.current.updates.dayComponents === undefined) {
                      dirtRef.current.updates.dayComponents = []
                    }
                    dirtRef.current.updates.dayComponents = Object.values(merge({}, keyBy(dirtRef.current.updates.dayComponents, "dayComponentId"), keyBy([{dayComponentId: dayComponent.dayComponentId, baseInfo: {amount: dayComponent.amount}}], "dayComponentId")));
                  }

                } else {
                  const newDayComponent = {dayComponentId: -createdDayComponentsIdRef.current, amount: value*1000, componentId: +columnId}
                  day.dayComponents.push(newDayComponent);

                  if (dirtRef.current.creates === undefined) {
                    dirtRef.current.creates={}
                  }
                  const newDirtyDayComponent = {amount: newDayComponent.amount, component_id: newDayComponent.componentId, temporaryId: newDayComponent.dayComponentId};
                  const newDirtyFeedDay = {day: day.day, dayId: day.dayId<0 ? undefined: day.dayId, temporaryId: day.dayId, dayComponents: [newDirtyDayComponent]}
                  const newDirtyMilestone = { feedCurveMilestoneId: feedCurveMilestone.feedCurveMilestoneId < 0 ? undefined : feedCurveMilestone.feedCurveMilestoneId, milestone_id: feedCurveMilestone.milestoneId, milestone_index: feedCurveMilestone.milestoneIndex, temporaryId: feedCurveMilestone.feedCurveMilestoneId,
                        feedDays: [newDirtyFeedDay]
                      }

                  if (dirtRef.current.creates.milestones) {
                    const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => (milestone.temporaryId === feedCurveMilestone.feedCurveMilestoneId));
                    if (dirtyMilestone) {
                      const dirtyDay = dirtyMilestone.feedDays?.find((feedDay) => feedDay.temporaryId === day.dayId);
                      if (dirtyDay) {
                        const dirtyDayComponent = dirtyDay.dayComponents?.find((dayComponent) => (dayComponent.temporaryId === newDayComponent.dayComponentId));
                        if (dirtyDayComponent) {
                          dirtyDayComponent.amount = newDirtyDayComponent.amount;
                        } else {
                          dirtyDay.dayComponents ? dirtyDay.dayComponents.push(newDirtyDayComponent) : dirtyDay.dayComponents = [newDirtyDayComponent];
                        }
                      } else {
                        dirtyMilestone.feedDays ? dirtyMilestone.feedDays.push(newDirtyFeedDay) : dirtyMilestone.feedDays = [newDirtyFeedDay];
                      }
                    } else {
                      dirtRef.current.creates.milestones.push(newDirtyMilestone)
                    }

                  } else {
                    dirtRef.current.creates.milestones = [newDirtyMilestone]
                  }
                  createdDayComponentsIdRef.current += 1;
                }
              }
            }
            setFeedCurve(cloneDeep(feedCurve));
            return
          }
          if (oldRow && (value === undefined || value === "")) {
            const feedCurveMilestone = feedCurve.milestones[oldRow.milestoneArrayIndex]
            if (feedCurveMilestone) {
              const day = feedCurveMilestone.feedDays[oldRow.dayIndex]
              if (day) {
                const dayToDeleteIndex = day.dayComponents.findIndex((dayComponents) => (dayComponents.componentId===+columnId));
                if (dirtRef.current.deletes === undefined) {
                  dirtRef.current.deletes = {};
                }
                if (dirtRef.current.deletes.dayComponents === undefined) {
                  dirtRef.current.deletes.dayComponents = [];
                }
                if (day.dayComponents[dayToDeleteIndex].dayComponentId < 0) {
                  const milestoneArrayIndex = dirtRef.current.creates?.milestones?.findIndex((milestone) => milestone.temporaryId === oldRow.milestoneId);
                  if (milestoneArrayIndex && dirtRef.current.creates?.milestones && dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays) {
                    dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays = dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays.filter((feedDay) => (feedDay.temporaryId !== day.dayComponents[dayToDeleteIndex].dayComponentId))
                  }
                } else {
                  dirtRef.current.deletes.dayComponents.push(day.dayComponents[dayToDeleteIndex].dayComponentId);
                }
                day.dayComponents.splice(dayToDeleteIndex, 1);
              }
            }
            setFeedCurve(cloneDeep(feedCurve));
            return
          }
        }
      },
      // #endregion
      // #region Delete function
      deleteRow: (rowId) => {
        const oldRow = data.get(rowId);
        if (feedCurve.milestones && oldRow) {
          const feedCurveMilestone = feedCurve.milestones[oldRow.milestoneArrayIndex]
          if (feedCurveMilestone) {
            feedCurveMilestone.feedDays.splice(oldRow.dayIndex, 1)
          }
        }
        setFeedCurve(cloneDeep(feedCurve));
        if (oldRow === undefined) {
          return;
        } 
        if (dirtRef.current.deletes === undefined) {
          dirtRef.current.deletes = {};
        }
        if (dirtRef.current.deletes.feedDays === undefined) {
          dirtRef.current.deletes.feedDays = [];
        }
        if (rowId < 0) {
          if (dirtRef.current.creates?.milestones) {
            const milestoneArrayIndex = dirtRef.current.creates.milestones.findIndex((milestone) => (milestone.temporaryId === oldRow.milestoneId));
            dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays = dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays?.filter((feedDay) => (feedDay.temporaryId !== rowId));
            if (dirtRef.current.creates.milestones[milestoneArrayIndex].feedDays?.length === 0) {
              dirtRef.current.creates.milestones.splice(milestoneArrayIndex, 1);
            }
          }
          return
        }

        dirtRef.current.deletes.feedDays.push(rowId)
      },
      // #endregion
      allowDeleteRowCheck: (row) => {return (!row.original.milestone)}
    }
  })

  const clearDirt = () => {
    dirtRef.current = {feedCurveId: feedCurve.feedCurveId};
    setFeedCurve(cloneDeep(originalFeedCurve));
  }
  
  const switchTable = () => {
    setDisableCurveNavigation(!editTable);
    setEditTable((editTable) => !editTable);
  };

  const saveChanges = () => {
    setDisableCurveNavigation(!editTable);
    maintainFeedCurve({data: dirtRef.current.updates},{data: dirtRef.current.deletes},{data:dirtRef.current.creates},{originalFeedCurve, optimisticFeedCurve: feedCurve})
    dirtRef.current = {feedCurveId: feedCurve.feedCurveId};
  }
  const togglePercentileEdit = () => {
    setIsPercentileEdit((old) => !old)
  }

  const percentileEditHeader = isPercentileEdit ? t("units.kg", {defaultValue: "KG"}) : t("units.pct", {defaultValue: "PCT"})

  const percentileEditIcon = isPercentileEdit ? glyphs.Kg({}) : glyphs.Percent({})

  return (
    // <frames.feedCurveTableContainer>
      <div style={{height: "100%", width: "100%", display: "grid", gridTemplateRows: "50px 1fr", boxSizing: "border-box"}}>
        <div style={{ gridArea: "1/1/2/2", backgroundColor: "transparent", display: "flex", justifyContent: "end", alignItems: "end", gap: "10px", padding: "0px 20px"}}>
          {(isScreenHeightMax || mobileXs) && 
          <Button 
            content={t("feedCurvePages.feedCurve.watchFeedCurve", {defaultValue: "watch feed curve"})} 
            buttonType={ButtonTypes.textButton}
            onClick={() => { if (watchFeedCurve) watchFeedCurve() }}
            overrides={{
              baseButton: {marginBottom: "10px"}
            }}
          />}
          {!editTable ?
            <ButtonList 
              contents={[percentileEditHeader, t("buttons.edit", {defaultValue: "edit"})]} 
              onClick={(i => {
                if (i === 0) togglePercentileEdit()
                else if (i === 1) switchTable()
              })}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[percentileEditIcon, glyphs.Edit({})]}
            />
            : <ButtonList 
                contents={[ t("buttons.save", {defaultValue: "save"}), t("buttons.cancel", {defaultValue: "cancel"}), percentileEditHeader, t("buttons.edit", {defaultValue: "edit"})]} 
                onClick={(i => {
                  if (i === 0) {saveChanges(); switchTable()}
                  else if (i === 1) {switchTable(); clearDirt();}
                  else if (i === 2) {
                  togglePercentileEdit()
                }
              })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Add({}), glyphs.Close({}), percentileEditIcon, glyphs.Edit({})]}
                disabled={[false, false, false, true]}
                overrides={(index) => ({
                  baseButton: {
                    backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary
                  },
                  disabledStyles: {
                    opacity: index === 3 ? 1 : 0.5
                  }
                })}
              />
          }
        </div>
        {/* FeedCurveTable */}
        <div style={{gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid "+colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
          <ReactiveTable table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("feed.feedCurve.noFeedCurve", {defaultValue: "no feedCurve found"})} />
        </div>
      </div>
    // </frames.feedCurveTableContainer>
  )
}
